import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "./api";
import StatusBadge, { LLM_TONE, TIER_TONE } from "@/components/brand/StatusBadge";
import { Time } from "./time";

const MODE_TONE = { off: "neutral", shadow: "info", on: "danger" };
const AGREEMENT_TONE = { agree: "safe", disagree: "danger", pending: "neutral" };

// Automated confirmation (Decision 32): the last 30 days' auto verdicts (live and shadow), Undo, and how often
// later human labels agreed (precision).
export default function AutoConfirm({ styles }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.autoConfirm(30).then((r) => { setData(r.data); setErr(""); }).catch((ex) => setErr(ex.message)), []);
  useEffect(() => { load(); }, [load]);
  const undo = async (id) => {
    setBusy(true);
    try { await api.undoAutoConfirm(id); setConfirming(null); await load(); } catch (ex) { setErr(ex.message); } finally { setBusy(false); }
  };
  const a = data?.agreement;
  const s = data?.settings;
  return (
    <div data-testid="auto-confirm-page">
      <h2>Auto-confirm</h2>
      {err && <div className="hs-text-danger">{err}</div>}
      {data && (
        <div style={styles.card}>
          <div style={styles.row}>
            <span>Mode</span>
            <StatusBadge testid="auto-confirm-mode" tone={MODE_TONE[data.mode] || "neutral"}>{data.mode}</StatusBadge>
            <span style={{ fontSize: 13, color: "var(--hs-text-muted)" }}>
              {data.mode === "off" && "Nothing is evaluated. Set AUTO_CONFIRM_MODE=shadow to measure it first."}
              {data.mode === "shadow" && "Evaluated and recorded only: nothing public changes."}
              {data.mode === "on" && `Live: public as malicious (basis "automated") ${s.grace_hours} h after it is recorded, unless undone.`}
            </span>
          </div>
          <div style={{ fontSize: 13, color: "var(--hs-text-muted)" }} data-testid="auto-confirm-thresholds">
            Rules score ≥ {s.min_rules_score} with a threat signal · primary model malicious with confidence ≥ {s.min_model_conf} ·
            second opinion ({s.second_opinion_provider}) malicious with a verified quote · {data.today} of {s.daily_max} today
          </div>
          <div style={{ ...styles.row, marginTop: 10, marginBottom: 0 }} data-testid="auto-confirm-precision">
            <strong>Agreement with later human labels:</strong>
            <span>{a.precision === null ? "no human labels yet" : `${(a.precision * 100).toFixed(1)}% precision`}</span>
            <span style={{ fontSize: 13, color: "var(--hs-text-muted)" }}>({a.agree} agree · {a.disagree} disagree · {a.pending} pending)</span>
          </div>
        </div>
      )}
      <div style={styles.card}>
        <table style={styles.tbl}>
          <thead><tr>
            <th style={styles.th}>Recorded</th><th style={styles.th}>Item</th><th style={styles.th}>Mode</th>
            <th style={styles.th}>Basis</th><th style={styles.th}>Public at</th><th style={styles.th}>Tier now</th>
            <th style={styles.th}>Human label</th><th style={styles.th}>State</th><th style={styles.th}></th>
          </tr></thead>
          <tbody>{(data?.rows || []).map((r) => (
            <tr key={r.verdict_id} data-testid={`auto-row-${r.verdict_id}`}>
              <td style={styles.td}><Time value={r.created_at} /></td>
              <td style={{ ...styles.td, fontFamily: "var(--hs-font-mono)" }}>
                <Link className="hs-link" to={`/admin/items/${r.item_id}`}>{r.item.registry_name || r.item.display_name || r.item.identifier || r.item_id}</Link>
                {!r.on_current_snapshot && <div style={{ fontSize: 12, color: "var(--hs-text-muted)" }}>content changed since</div>}
              </td>
              <td style={styles.td}><StatusBadge tone={MODE_TONE[r.mode] || "neutral"}>{r.mode}</StatusBadge></td>
              <td style={{ ...styles.td, fontSize: 13 }}>
                rules {Number(r.basis.rules_score || 0).toFixed(2)} · model {Number(r.basis.model_conf || 0).toFixed(2)} ·{" "}
                {r.basis.quotes_verified} verified quote(s){r.basis.second_opinion_model ? ` (${r.basis.second_opinion_model})` : ""}
              </td>
              <td style={styles.td}>{r.mode === "on" ? <Time value={r.public_at} /> : "—"}</td>
              <td style={styles.td}>{r.item.current_tier
                ? <StatusBadge tone={TIER_TONE[r.item.current_tier] || "neutral"}>{r.item.current_tier}</StatusBadge> : "—"}</td>
              <td style={styles.td}>
                {r.human_label ? <StatusBadge tone={LLM_TONE[r.human_label] || "neutral"}>{r.human_label}</StatusBadge> : "—"}{" "}
                <StatusBadge testid={`auto-agreement-${r.verdict_id}`} tone={AGREEMENT_TONE[r.agreement]}>{r.agreement}</StatusBadge>
              </td>
              <td style={styles.td}>{r.state === "undone"
                ? <span title={r.undone?.at || ""}>undone by {r.undone?.by || "?"} ({r.undone?.reason})</span> : "active"}</td>
              <td style={styles.td}>{r.state === "active" && (confirming === r.verdict_id
                ? <>
                    <button type="button" style={styles.btn} disabled={busy} data-testid={`auto-undo-confirm-${r.verdict_id}`}
                            onClick={() => undo(r.verdict_id)}>Confirm undo</button>{" "}
                    <button type="button" style={styles.btnDim} onClick={() => setConfirming(null)}>Cancel</button>
                  </>
                : <button type="button" style={styles.btnDim} data-testid={`auto-undo-${r.verdict_id}`}
                          onClick={() => setConfirming(r.verdict_id)}>Undo</button>)}</td>
            </tr>))}
          {data && data.rows.length === 0 && <tr><td style={styles.td} colSpan={9}>No auto verdicts in the last {data.days} days.</td></tr>}</tbody>
        </table>
      </div>
    </div>
  );
}
